import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import PropertyCard, { type PropertyCardProps } from '@/components/PropertyCard';
import { useIsMobile } from '@/hooks/use-mobile';

interface FeaturedPropertiesProps {
  title?: string;
  subtitle?: string;
  properties: PropertyCardProps[];
  showFilters?: boolean;
  showViewAll?: boolean;
  onViewAll?: () => void;
  onPropertyClick?: (propertyId: string | number) => void;
  filterOptions?: { id: string; label: string }[];
  className?: string;
  variant?: 'carousel' | 'grid';
  maxVisible?: number;
  dots?: boolean;
}

const FeaturedProperties: React.FC<FeaturedPropertiesProps> = ({
  title = "Imóveis em Destaque",
  subtitle,
  properties,
  showFilters = true,
  showViewAll = true,
  onViewAll,
  onPropertyClick,
  filterOptions = [
    { id: 'todos', label: 'Todos' },
    { id: 'alugar', label: 'Alugar' },
    { id: 'comprar', label: 'Comprar' }
  ],
  className,
  variant = 'carousel',
  maxVisible = 5,
  dots = false
}) => {
  const isMobile = useIsMobile();
  const [activeFilter, setActiveFilter] = useState(filterOptions[0]?.id || 'todos');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1200); // Default width para evitar layout shifts
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Filter properties based on active filter
  const filteredProperties = properties.filter(property => {
    if (activeFilter === 'todos') return true;
    if (activeFilter === 'alugar') return property.saleType === 'Locação';
    if (activeFilter === 'comprar') return property.saleType === 'Venda';
    return true;
  });

  // Calcular dimensões baseado no design mobile/desktop
  // No mobile usamos a largura real do card compacto para evitar espaços "mortos" no wrapper
  const compactCardWidth = 270; // Deve acompanhar o variant "compact" de PropertyCard
  const cardWidth = isMobile ? compactCardWidth : 310;
  const cardGap = isMobile ? 8 : 24; // gap reduzido ~10% do que estava
  const cardWithGap = cardWidth + cardGap;
  
  // Mobile: mostrar 1 card + metade do próximo | Desktop: cálculo normal
  const visibleCards = isMobile ? 1.5 : Math.max(1.3, containerWidth / cardWithGap);
  const maxScrollIndex = Math.max(0, filteredProperties.length - Math.floor(visibleCards));

  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxScrollIndex, prev + 1));
  };

  const canScrollPrev = currentIndex > 0;
  const canScrollNext = currentIndex < maxScrollIndex;

  // Touch/Drag handlers (antigo mobile) — deixados aqui mas desativados
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isMobile) return;
    setIsDragging(true);
    setStartX(e.pageX - (carouselRef.current?.offsetLeft || 0));
    setScrollLeft(currentIndex * cardWithGap);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (carouselRef.current?.offsetLeft || 0));
    setScrollLeft(currentIndex * cardWithGap);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isMobile || cardWithGap === 0) return;
    e.preventDefault();
    const x = e.pageX - (carouselRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 1.5;
    const newScrollLeft = scrollLeft - walk;
    const newIndex = Math.round(newScrollLeft / cardWithGap);
    const clampedIndex = Math.max(0, Math.min(newIndex, maxScrollIndex));
    setCurrentIndex(clampedIndex);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !isMobile || cardWithGap === 0) return;
    const x = e.touches[0].pageX - (carouselRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 1.5;
    const newScrollLeft = scrollLeft - walk;
    const newIndex = Math.round(newScrollLeft / cardWithGap);
    const clampedIndex = Math.max(0, Math.min(newIndex, maxScrollIndex));
    setCurrentIndex(clampedIndex);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Scroll nativo no mobile com scroll-snap
  const handleScroll = () => {
    if (!isMobile || !carouselRef.current || cardWithGap === 0) return;
    const idx = Math.round(carouselRef.current.scrollLeft / cardWithGap);
    const clampedIndex = Math.max(0, Math.min(idx, maxScrollIndex));
    setCurrentIndex(clampedIndex);
  };

  const scrollToIndex = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, maxScrollIndex));
    if (isMobile && carouselRef.current) {
      carouselRef.current.scrollTo({ left: clamped * cardWithGap, behavior: 'smooth' });
    } else {
      setCurrentIndex(clamped);
    }
  };

  // Effect para detectar tamanho do container
  React.useEffect(() => {
    const updateWidth = () => {
      // Calcular largura disponível baseado no viewport
      const viewportWidth = window.innerWidth;
      let sideMargins;
      
      // Responsividade aprimorada para diferentes tamanhos
      if (viewportWidth >= 1024) {
        sideMargins = 80; // lg:px-10 = 40px cada lado
      } else if (viewportWidth >= 768) {
        sideMargins = 64; // md: margens médias
      } else {
        sideMargins = 32; // px-4 = 16px cada lado em mobile
      }
      
      const availableWidth = Math.max(280, viewportWidth - sideMargins); // Min width 280px para mobile
      setContainerWidth(availableWidth);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Reset do currentIndex quando trocar filtro
  React.useEffect(() => {
    setCurrentIndex(0);
  }, [activeFilter]);

  return (
    <section className={cn(
      "py-14",
      isMobile ? "px-4" : "px-4 lg:px-10",
      className
    )}>
      {/* Header - Mobile vs Desktop Layout */}
      {isMobile ? (
        // Mobile Layout - Título + Ver tudo em uma linha, botões embaixo
        <div className="content-stretch flex flex-col gap-[16px] items-start justify-center relative w-full mb-6">
          {/* Título e Ver tudo */}
          <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
            <h2 className="font-black leading-[normal] not-italic relative shrink-0 text-neutral-black text-[22px] text-nowrap whitespace-pre">
              {title}
            </h2>
            {showViewAll && (
              <button 
                onClick={onViewAll}
                className="content-stretch flex gap-[4px] items-center relative shrink-0 hover:opacity-80 transition-opacity"
              >
                <span className="font-normal leading-none not-italic relative shrink-0 text-neutral-black text-[14px] text-nowrap whitespace-pre">
                  Ver tudo
                </span>
                <ArrowRight className="w-[16px] h-[16px] text-neutral-black" />
              </button>
            )}
          </div>

          {/* Botões de categoria embaixo */}
          {showFilters && filterOptions.length > 1 && (
            <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
              <div className="h-[32px] relative rounded-[24px] shrink-0">
                <div className="box-border content-stretch flex h-[32px] items-start justify-start overflow-clip p-[1px] relative border border-[rgba(0,0,0,0.12)] rounded-[24px]">
                  {filterOptions.map((option, idx) => (
                    <button
                      key={option.id}
                      onClick={() => setActiveFilter(option.id)}
                      className={cn(
                        "h-full relative shrink-0 px-2 flex items-center justify-center rounded-[24px]",
                        activeFilter === option.id
                          ? "bg-neutral-gray-bg border border-neutral-gray-dark"
                          : "bg-transparent"
                      )}
                    >
                      <span className={cn(
                        "text-[12px] text-center tracking-[0.5px] w-[50px]",
                        activeFilter === option.id 
                          ? "font-bold text-neutral-dark leading-[16px]"
                          : "font-medium text-neutral-dark leading-[16px]"
                      )}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Desktop Layout - Original layout horizontal
        <div className="flex items-center gap-6 mb-8 w-full max-w-full">
          {/* Title */}
          <h2 className="text-[32px] font-black text-neutral-black leading-normal whitespace-nowrap">
            {title}
          </h2>

          {/* Tabs Container */}
          <div className="flex-1 min-w-0">
            {showFilters && filterOptions.length > 1 && (
              <div className="h-10 p-0.5 rounded-[32px] border border-black/12 inline-flex">
                {filterOptions.map((option, idx) => (
                  <button
                    key={option.id}
                    onClick={() => setActiveFilter(option.id)}
                    className={cn(
                      "flex items-center h-full px-3 lg:px-4 rounded-[28px] transition-colors",
                      activeFilter === option.id
                        ? "bg-neutral-gray-bg border border-neutral-gray font-bold text-neutral-black"
                        : "bg-transparent text-neutral-black font-medium",
                      idx === 0 && "mr-0.5"
                    )}
                  >
                    <span className="w-[66px] text-center text-sm leading-[18px] tracking-[1.25px]">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View All Button */}
          {showViewAll && (
            <button 
              onClick={onViewAll}
              className="flex items-center gap-2 text-neutral-black hover:opacity-80 transition-opacity"
            >
              <span className="text-base font-normal whitespace-nowrap">Ver tudo</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          )}
        </div>
      )}

      {/* Properties Display - Following Figma Layout */}
      {filteredProperties.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Nenhum imóvel encontrado para o filtro selecionado.</p>
        </div>
      ) : variant === 'carousel' ? (
        <div className={cn(
          "relative w-full",
          isMobile && "overflow-hidden"
        )}>
          <div 
            ref={carouselRef}
            className={cn(
              "relative",
              isMobile 
                ? "overflow-x-auto py-4 snap-x snap-mandatory"
                : "overflow-hidden py-8"
            )}
            onScroll={handleScroll}
          >
            <div
              className={cn(
                "flex h-full",
                !isMobile && "transition-transform duration-300 ease-in-out"
              )}
              style={{
                ...(isMobile ? {} : { transform: `translateX(-${currentIndex * cardWithGap}px)` }),
                columnGap: `${cardGap}px`,
                width: isMobile ? 'auto' : `${filteredProperties.length * cardWithGap}px`
              }}
            >
              {filteredProperties.map((property, index) => (
                <div
                  key={property.id}
                  className={cn(
                    "flex-shrink-0",
                    isMobile && "snap-start"
                  )}
                  style={{ 
                    width: `${cardWidth}px`,
                    minWidth: `${cardWidth}px`
                  }}
                >
                  <PropertyCard
                    {...property}
                    variant={isMobile ? "compact" : "featured"}
                    className="h-full w-full"
                    onClick={() => onPropertyClick?.(property.id)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - Only Desktop */}
          {!isMobile && filteredProperties.length > Math.floor(visibleCards) && (
            <>
              <button
                onClick={handlePrev}
                disabled={!canScrollPrev}
                className={cn(
                  "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 z-10 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-200",
                  canScrollPrev
                    ? "hover:bg-gray-50 text-gray-700"
                    : "text-gray-300 cursor-not-allowed"
                )}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={handleNext}
                disabled={!canScrollNext}
                className={cn(
                  "absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 z-10 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-200",
                  canScrollNext
                    ? "hover:bg-gray-50 text-gray-700"
                    : "text-gray-300 cursor-not-allowed"
                )}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Dots Indicator */}
          {dots && filteredProperties.length > Math.floor(visibleCards) && maxScrollIndex > 0 && (
            <div className={cn(
              "flex justify-center gap-2",
              isMobile ? "mt-4" : "mt-3"
            )}>
              {Array.from({ length: maxScrollIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={cn(
                    "rounded-full transition-colors",
                    isMobile ? "w-2 h-2" : "w-2 h-2",
                    currentIndex === index
                      ? "bg-neutral-black"
                      : "bg-neutral-gray-light/30 hover:bg-neutral-gray-light/50"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      ) : variant === 'grid' ? (
        /* Grid Layout - Responsive Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {filteredProperties.slice(0, maxVisible).map((property) => (
            <div key={property.id} className="w-full max-w-[310px] mx-auto">
              <PropertyCard
                {...property}
                variant="featured"
                className="h-full"
                onClick={() => onPropertyClick?.(property.id)}
              />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export { FeaturedProperties, type FeaturedPropertiesProps };
export default FeaturedProperties;